/**
 * Idle/grace timing for the solo "are you still there?" auto-kill. DOM-free and
 * unit-testable. The wiring layer (main.js) supplies onPrompt/onExpire and feeds
 * user activity via noteActivity()/affirm(). See
 * docs/superpowers/specs/2026-06-15-solo-idle-afk-kill-design.md
 */
export const IDLE_MS = 60 * 60 * 1000;  // 1 hour with no interaction
export const GRACE_MS = 120 * 1000;     // 120s to affirm presence

export class IdleWatcher {
  constructor({ idleMs = IDLE_MS, graceMs = GRACE_MS, onPrompt = () => {}, onExpire = () => {} } = {}) {
    this._idleMs = idleMs;
    this._graceMs = graceMs;
    this._onPrompt = onPrompt;
    this._onExpire = onExpire;
    this._idleTimer = null;
    this._graceTimer = null;
    this._prompting = false;
    this._running = false;
  }

  start() { this._running = true; this._prompting = false; this._armIdle(); }

  stop() { this._running = false; this._prompting = false; this._clearTimers(); }

  /** User interacted. Re-arms the idle timer. No-op while prompting or stopped. */
  noteActivity() { if (this._running && !this._prompting) this._armIdle(); }

  /** User explicitly affirmed presence. Cancels the countdown, restarts idle. */
  affirm() {
    if (!this._running) return;
    this._prompting = false;
    this._clearTimers();
    this._armIdle();
  }

  isPrompting() { return this._prompting; }

  _armIdle() {
    if (this._idleTimer) clearTimeout(this._idleTimer);
    this._idleTimer = setTimeout(() => this._enterPrompt(), this._idleMs);
  }

  _enterPrompt() {
    this._prompting = true;
    this._onPrompt();
    this._graceTimer = setTimeout(() => this._expire(), this._graceMs);
  }

  _expire() {
    this._prompting = false;
    this._clearTimers();
    this._running = false;
    this._onExpire();
  }

  _clearTimers() {
    if (this._idleTimer) { clearTimeout(this._idleTimer); this._idleTimer = null; }
    if (this._graceTimer) { clearTimeout(this._graceTimer); this._graceTimer = null; }
  }
}
